/**
 * Industry Defaults and Calculation Constants Module
 * Contains downtime costs, resurfacing intervals, and material calculation constants
 */

export type IndustryType = 'manufacturing' | 'automotive' | 'datacenter' | 'hydro' | 'warehouse' | 'distribution-center' | 'transportation' | 'utilities' | 'retail' | 'mining' | 'custom';
export type MarkupType = 'distributor' | 'readymix' | 'custom';

/**
 * Industry-specific downtime costs per hour (in USD)
 */
export interface IndustryDefaults {
  downtimeCostPerHour: number;
  downtimeCostRange: [number, number]; // For display purposes [min, max]
  resurfacingIntervalYears: {
    standard: number; // For thickness < 8 inches
    thick: number;    // For thickness >= 8 inches
  };
  displayName: string;
  description: string;
}

export const INDUSTRY_DEFAULTS: Record<Exclude<IndustryType, 'custom'>, IndustryDefaults> = {
  manufacturing: {
    downtimeCostPerHour: 260000,
    downtimeCostRange: [200000, 320000],
    resurfacingIntervalYears: {
      standard: 5,
      thick: 7
    },
    displayName: 'Manufacturing',
    description: 'Manufacturing facilities and production plants'
  },
  automotive: {
    downtimeCostPerHour: 3000000, // $2.3-3M range, using upper bound
    downtimeCostRange: [2300000, 3000000],
    resurfacingIntervalYears: {
      standard: 4,
      thick: 5
    },
    displayName: 'Automotive',
    description: 'Automotive manufacturing and assembly plants'
  },
  datacenter: {
    downtimeCostPerHour: 600000,
    downtimeCostRange: [500000, 700000],
    resurfacingIntervalYears: {
      standard: 8,
      thick: 8 // Same for data centers regardless of thickness
    },
    displayName: 'Data Center',
    description: 'Data centers and server facilities'
  },
  hydro: {
    downtimeCostPerHour: 20000,
    downtimeCostRange: [15000, 25000],
    resurfacingIntervalYears: {
      standard: 5,
      thick: 10
    },
    displayName: 'Hydroelectric',
    description: 'Hydroelectric power generation facilities'
  },
  warehouse: {
    downtimeCostPerHour: 75000, // Mid-range of $50k-$100k
    downtimeCostRange: [50000, 100000],
    resurfacingIntervalYears: {
      standard: 7,
      thick: 10
    },
    displayName: 'Warehouse',
    description: 'Logistics and storage facilities'
  },
  'distribution-center': {
    downtimeCostPerHour: 225000, // Mid-range of $150k-$300k
    downtimeCostRange: [150000, 300000],
    resurfacingIntervalYears: {
      standard: 6,
      thick: 8
    },
    displayName: 'Distribution Center',
    description: 'High-volume distribution and fulfillment centers'
  },
  transportation: {
    downtimeCostPerHour: 140000, // Mid-range of $80k-$200k
    downtimeCostRange: [80000, 200000],
    resurfacingIntervalYears: {
      standard: 7,
      thick: 10
    },
    displayName: 'Transportation Hub',
    description: 'Airports, rail terminals, and transportation hubs'
  },
  utilities: {
    downtimeCostPerHour: 55000, // Mid-range of $30k-$80k
    downtimeCostRange: [30000, 80000],
    resurfacingIntervalYears: {
      standard: 10,
      thick: 15
    },
    displayName: 'Utilities',
    description: 'Power plants, water treatment, and utility facilities'
  },
  retail: {
    downtimeCostPerHour: 12500, // Mid-range of $5k-$20k
    downtimeCostRange: [5000, 20000],
    resurfacingIntervalYears: {
      standard: 5,
      thick: 7
    },
    displayName: 'Retail',
    description: 'Retail stores and commercial spaces'
  },
  mining: {
    downtimeCostPerHour: 400000, // Mid-range of $300k-$500k
    downtimeCostRange: [300000, 500000],
    resurfacingIntervalYears: {
      standard: 3,
      thick: 5
    },
    displayName: 'Mining',
    description: 'Mining operations and heavy industrial facilities'
  }
};

/**
 * Material calculation constants
 */
export const MATERIAL_CONSTANTS = {
  // Base cost per pound of Kryton material
  BASE_COST_PER_LB: 0.32,
  
  // Default material density (lbs per cubic yard)
  DEFAULT_LBS_PER_YD3: 66,
  
  // Cubic feet per cubic yard conversion
  CUBIC_FEET_PER_YD3: 27,
  
  // Square feet per square yard
  SQ_FT_PER_SQ_YD: 9,
  
  // Inches per foot
  INCHES_PER_FOOT: 12,
  
  // Resurfacing cost per square foot (standard flooring)
  RESURFACING_COST_PER_SQFT: 6.0,
  
  // Freight calculation constants
  FREIGHT: {
    // Kilograms per pallet (standard pallet weight capacity)
    KG_PER_PALLET: 1200,
    
    // Pallets per truck (standard truck capacity)
    PALLETS_PER_TRUCK: 16,
    
    // Pounds to kilograms conversion
    LBS_TO_KG: 0.453592,
    
    // Default freight rate when API fails (USD)
    DEFAULT_FREIGHT_RATE_USD: 4500,
    
    // Default freight rate when API fails (CAD)
    DEFAULT_FREIGHT_RATE_CAD: 6000
  },
  
  // Unit conversion constants
  CONVERSIONS: {
    // Square meters to square feet
    SQ_M_TO_SQ_FT: 10.7639,
    
    // Millimeters to inches
    MM_TO_INCHES: 0.0393701,
    
    // Cubic meters to cubic yards
    CUBIC_M_TO_CUBIC_YD: 1.30795
  },
  
  // Kryton specific constants
  KRYTON: {
    // Reduction in resurfacing frequency (50% less frequent)
    RESURFACING_FREQUENCY_REDUCTION: 0.5,
    
    // Dosage range (percentage of standard application)
    MIN_DOSAGE_PCT: 33,
    MAX_DOSAGE_PCT: 125,
    STANDARD_DOSAGE_PCT: 100
  }
} as const;

/**
 * Markup profiles for different business models
 */
export interface MarkupProfile {
  percentage: number;
  displayName: string;
  description: string;
}

export const MARKUP_PROFILES: Record<Exclude<MarkupType, 'custom'>, MarkupProfile> = {
  distributor: {
    percentage: 20,
    displayName: 'Distributor (20%)',
    description: '20% markup for distribution business model'
  },
  readymix: {
    percentage: 100,
    displayName: 'Ready-Mix Producer (100%)',
    description: '100% markup for ready-mix production business model'
  }
};

/**
 * Get industry defaults for a specific industry type
 */
export function getIndustryDefaults(industry: IndustryType): IndustryDefaults | null {
  if (industry === 'custom') {
    return null;
  }
  return INDUSTRY_DEFAULTS[industry];
}

/**
 * Calculate resurfacing interval based on industry and slab thickness
 */
export function getResurfacingInterval(
  industry: IndustryType, 
  thicknessInches: number
): number {
  const defaults = getIndustryDefaults(industry);
  if (!defaults) {
    // Default for custom industry
    return thicknessInches >= 8 ? 7 : 5;
  }
  
  return thicknessInches >= 8 
    ? defaults.resurfacingIntervalYears.thick 
    : defaults.resurfacingIntervalYears.standard;
}

/**
 * Get markup percentage for a markup type
 */
export function getMarkupPercentage(markupType: MarkupType, customPercentage?: number): number {
  if (markupType === 'custom') {
    return customPercentage ?? 0;
  }
  return MARKUP_PROFILES[markupType].percentage;
}

/**
 * Calculate material weight in pounds for given volume and dosage
 */
export function calculateMaterialWeight(
  volumeCubicYards: number, 
  dosagePercentage: number = MATERIAL_CONSTANTS.KRYTON.STANDARD_DOSAGE_PCT
): number {
  const adjustedLbsPerYd3 = MATERIAL_CONSTANTS.DEFAULT_LBS_PER_YD3 * (dosagePercentage / 100);
  return volumeCubicYards * adjustedLbsPerYd3;
}

/**
 * Calculate material cost before markup
 */
export function calculateRawMaterialCost(
  volumeCubicYards: number,
  dosagePercentage: number = MATERIAL_CONSTANTS.KRYTON.STANDARD_DOSAGE_PCT
): number {
  const totalWeight = calculateMaterialWeight(volumeCubicYards, dosagePercentage);
  return totalWeight * MATERIAL_CONSTANTS.BASE_COST_PER_LB;
}

/**
 * Calculate material cost with markup applied
 */
export function calculateMaterialCostWithMarkup(
  volumeCubicYards: number,
  dosagePercentage: number,
  markupType: MarkupType,
  customMarkup?: number
): number {
  const rawCost = calculateRawMaterialCost(volumeCubicYards, dosagePercentage);
  const markupPercentage = getMarkupPercentage(markupType, customMarkup);
  return rawCost * (1 + markupPercentage / 100);
}

/**
 * Convert weight to number of pallets for freight calculation
 */
export function weightToPallets(weightKg: number): number {
  return Math.ceil(weightKg / MATERIAL_CONSTANTS.FREIGHT.KG_PER_PALLET);
}

/**
 * Convert weight from pounds to kilograms
 */
export function poundsToKilograms(pounds: number): number {
  return pounds * MATERIAL_CONSTANTS.FREIGHT.LBS_TO_KG;
}

/**
 * Calculate freight cost based on pallets and rate per truck
 */
export function calculateFreightCost(numPallets: number, ratePerTruck: number): number {
  return (ratePerTruck / MATERIAL_CONSTANTS.FREIGHT.PALLETS_PER_TRUCK) * numPallets;
}

/**
 * Validate dosage percentage is within acceptable range
 */
export function validateDosagePercentage(dosage: number): boolean {
  return dosage >= MATERIAL_CONSTANTS.KRYTON.MIN_DOSAGE_PCT && 
         dosage <= MATERIAL_CONSTANTS.KRYTON.MAX_DOSAGE_PCT;
}

/**
 * Validate markup percentage is reasonable (0-500%)
 */
export function validateMarkupPercentage(markup: number): boolean {
  return markup >= 0 && markup <= 500;
}

/**
 * Get all available industry options for dropdowns
 */
export function getIndustryOptions(): Array<{ value: IndustryType; label: string }> {
  return [
    ...Object.entries(INDUSTRY_DEFAULTS).map(([key, value]) => ({
      value: key as IndustryType,
      label: value.displayName
    })),
    { value: 'custom', label: 'Custom' }
  ];
}

/**
 * Get all available markup options for dropdowns
 */
export function getMarkupOptions(): Array<{ value: MarkupType; label: string }> {
  return [
    ...Object.entries(MARKUP_PROFILES).map(([key, value]) => ({
      value: key as MarkupType,
      label: value.displayName
    })),
    { value: 'custom', label: 'Custom' }
  ];
}